import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, CalendarClock, Plus, Search } from 'lucide-react';
import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import { SOURCES, metaOf, sourceLabel, useCreateLead, useLeads, type Lead, type LeadScope } from '@/lib/leads';
import { SERVICE_OPTIONS, serviceLabel } from '@/lib/contact';
import { ago, when } from '@/lib/format';
import { Page, PageHeader } from '@/components/Shell';
import { Empty, ListSkeleton, LoadError, StatusChip } from '@/components/ui';

const SCOPES: { value: LeadScope; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'won', label: 'Won' },
  { value: 'closed', label: 'Lost & spam' },
  { value: 'all', label: 'All' },
];

export function EnquiriesScreen({ scope, q }: { scope: LeadScope; q: string }) {
  const navigate = useNavigate({ from: '/enquiries' });
  const leads = useLeads(scope);
  const [query, setQuery] = useState(q);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle || !leads.data) return leads.data ?? [];
    return leads.data.filter((l) =>
      [l.name, l.company, l.email, l.phone, l.bottleneck, l.ref].some((v) => v?.toLowerCase().includes(needle)),
    );
  }, [leads.data, query]);

  return (
    <>
      <PageHeader title="Enquiries">
        <Link to="/enquiries/new" className="btn btn-primary">
          <Plus className="size-4" aria-hidden /> <span className="max-sm:sr-only">Add lead</span>
        </Link>
      </PageHeader>
      <Page>
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="-mx-1 flex gap-1 overflow-x-auto px-1" role="tablist" aria-label="Which enquiries">
            {SCOPES.map((s) => (
              <Link
                key={s.value}
                to="/enquiries"
                search={{ scope: s.value, q: query || undefined }}
                role="tab"
                aria-selected={scope === s.value}
                className={`btn btn-sm shrink-0 ${scope === s.value ? 'btn-ink' : 'btn-ghost'}`}
              >
                {s.label}
              </Link>
            ))}
          </div>
          <label className="relative block sm:w-72">
            <span className="sr-only">Search enquiries</span>
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-faint" aria-hidden />
            <input
              type="search"
              className="input !min-h-10 !pl-9 text-sm"
              placeholder="Name, company, phone…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                navigate({ search: (s) => ({ ...s, q: e.target.value || undefined }), replace: true });
              }}
            />
          </label>
        </div>

        {leads.isPending ? (
          <ListSkeleton />
        ) : leads.error ? (
          <LoadError error={leads.error} retry={() => leads.refetch()} />
        ) : shown.length === 0 ? (
          query ? (
            <Empty title={`Nothing matches "${query}"`}>Search looks at names, companies, phone, email and what they wrote.</Empty>
          ) : scope === 'open' ? (
            <Empty
              title="No open enquiries"
              action={
                <Link to="/enquiries/new" className="btn btn-quiet">
                  <Plus className="size-4" aria-hidden /> Add a lead from WhatsApp or a call
                </Link>
              }
            >
              Form enquiries from bitbew.com land here by themselves. Leads that arrive on WhatsApp, by phone or on
              Instagram can be added by hand, so every conversation is in one list.
            </Empty>
          ) : (
            <Empty title="None here">Enquiries move here when you change their status.</Empty>
          )
        ) : (
          <ul className="divide-y divide-edge overflow-hidden rounded-lg border border-edge bg-bg-raised">
            {shown.map((l) => (
              <LeadRow key={l.id} lead={l} />
            ))}
          </ul>
        )}
      </Page>
    </>
  );
}

function LeadRow({ lead }: { lead: Lead }) {
  const meta = metaOf(lead);
  const service = serviceLabel(meta.interest);
  const due = lead.next_action_at && new Date(lead.next_action_at).getTime() <= Date.now();
  const unread = !lead.read_at;

  return (
    <li>
      <Link
        to="/enquiries/$leadId"
        params={{ leadId: lead.id }}
        className="grid grid-cols-[0.5rem_1fr_auto] items-start gap-x-3 px-4 py-3.5 transition-colors duration-150 hover:bg-bg-sunk/60 sm:grid-cols-[0.5rem_minmax(0,1fr)_9rem_7.5rem_4rem] sm:items-center"
      >
        <span
          className={`mt-2 size-2 rounded-full sm:mt-0 ${unread ? 'bg-ink' : 'bg-transparent'}`}
          aria-label={unread ? 'Unread' : undefined}
        />
        <div className="min-w-0">
          <p className={`truncate ${unread ? 'font-semibold' : ''}`}>
            {lead.name}
            {lead.company && <span className="font-normal text-fg-dim"> · {lead.company}</span>}
          </p>
          <p className="truncate text-sm text-fg-dim">
            {[service, lead.source !== 'website' ? sourceLabel(lead.source) : null, lead.bottleneck]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {lead.next_action_at && (
            <p className={`mt-1 flex items-center gap-1.5 text-xs ${due ? 'font-semibold text-warn' : 'text-fg-dim'}`}>
              <CalendarClock className="size-3.5" aria-hidden />
              <span className="tnum">{when(lead.next_action_at)}</span>
              {lead.next_action_note && <span className="truncate">· {lead.next_action_note}</span>}
            </p>
          )}
        </div>
        <span className="hidden truncate text-sm text-fg-dim sm:block">{service ?? '—'}</span>
        <span className="row-span-2 sm:row-span-1">
          <StatusChip status={lead.status} />
        </span>
        <time dateTime={lead.created_at} className="tnum hidden text-right text-sm whitespace-nowrap text-fg-faint sm:block">
          {ago(lead.created_at)}
        </time>
      </Link>
    </li>
  );
}

/* --------------------------------------------------------------- add lead */

export function NewLeadScreen() {
  const navigate = useNavigate();
  const create = useCreateLead();
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const get = (k: string) => String(f.get(k) ?? '').trim();
    const phone = get('phone');
    const email = get('email').toLowerCase();
    if (!get('name')) return setError('A name is needed, even just a first name.');
    if (!phone && !email) return setError('Add a phone number or an email, so there is a way to reach them.');
    const parsed = phone ? parsePhoneNumberFromString(phone, 'IN') : undefined;
    if (phone && !parsed?.isValid()) return setError(`"${phone}" doesn't look like a phone number. Include the country code if it isn't Indian.`);

    setError(null);
    const interest = get('interest');
    create.mutate(
      {
        name: get('name'),
        phone: phone || null,
        phone_e164: parsed?.number ?? null,
        email: email || null,
        company: get('company'),
        bottleneck: get('need'),
        source: get('source') || 'whatsapp',
        metadata: { interest: interest || null, source_page: null },
        read_at: new Date().toISOString(),
      },
      {
        onSuccess: (id) => navigate({ to: '/enquiries/$leadId', params: { leadId: id } }),
        onError: (err) => setError(err.message),
      },
    );
  }

  return (
    <>
      <PageHeader
        title="Add a lead"
        back={
          <Link to="/enquiries" className="btn btn-ghost btn-icon -ml-2" aria-label="Back to enquiries">
            <ArrowLeft className="size-5" aria-hidden />
          </Link>
        }
      />
      <Page>
        <form onSubmit={onSubmit} className="grid max-w-xl gap-5" noValidate>
          <p className="text-fg-dim">
            For enquiries that arrive on WhatsApp, by phone or on Instagram. Form enquiries add themselves.
          </p>
          <div className="field">
            <label className="label" htmlFor="name">
              Name
            </label>
            <input id="name" name="name" className="input" autoComplete="off" required />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="field">
              <label className="label" htmlFor="phone">
                Phone or WhatsApp
              </label>
              <input id="phone" name="phone" className="input num" inputMode="tel" placeholder="98140 12345" />
            </div>
            <div className="field">
              <label className="label" htmlFor="email">
                Email
              </label>
              <input id="email" name="email" type="email" className="input" />
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="field">
              <label className="label" htmlFor="company">
                Business
              </label>
              <input id="company" name="company" className="input" />
            </div>
            <div className="field">
              <label className="label" htmlFor="source">
                Where it came from
              </label>
              <select id="source" name="source" className="input" defaultValue="whatsapp">
                {SOURCES.filter((s) => s.value !== 'website').map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="field">
            <label className="label" htmlFor="interest">
              What they want
            </label>
            <select id="interest" name="interest" className="input" defaultValue="">
              <option value="">Not sure yet</option>
              {SERVICE_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="label" htmlFor="need">
              What they said
            </label>
            <textarea id="need" name="need" className="input" rows={4} placeholder="In their words, if you have them." />
          </div>
          {error && (
            <p role="alert" className="error-text">
              {error}
            </p>
          )}
          <div className="flex gap-3">
            <button type="submit" className="btn btn-primary btn-lg" disabled={create.isPending}>
              {create.isPending ? 'Saving…' : 'Save lead'}
            </button>
            <Link to="/enquiries" className="btn btn-ghost btn-lg">
              Cancel
            </Link>
          </div>
        </form>
      </Page>
    </>
  );
}
